/**
 * URL state — the wire format for a shareable view of the tracker.
 *
 * Sibling of the budget widget's utils/embedConfig.ts, and the same doctrine:
 * explicit query params under the API's OWN names (see buildParams in
 * utils/api.ts), defaults omitted, no opaque blob. A shared link is then
 * readable in a server log, hand-editable, and extending it later is an
 * additive optional param — no versioning, no server-stored short id that
 * every published link would depend on.
 *
 * Two departures from embedConfig, both forced by where this runs:
 *
 *   - The free-text `search` IS encoded here. Budget leaves its `q` out
 *     because the API strips it, so an embed would print a caption claiming a
 *     filter that was never applied. Here FilterQuerySchema.search exists and
 *     buildWhereClause applies it, and "all battery-cell projects" is exactly
 *     the link a journalist wants to send. The cost — query-string variants —
 *     is contained by never LINKING a search URL from anywhere crawlable.
 *
 *   - Decoding is validation, not parsing. The embed page owned its own URL;
 *     we are one block on a WordPress page and can be handed anything. The
 *     investments API answers 400 on a malformed filter, so one bad param out
 *     of a stale link would break every request the widget makes and show an
 *     error instead of data. Anything that fails its check is dropped.
 *
 * Deliberately NOT in the URL, and why:
 *
 *   - aggregateClusterIds — unbounded arrays of database ids: unreadable,
 *     hundreds of characters, and stale the moment the data reloads.
 *   - selectedCountry / selectedWahlkreis — the labels are stable, but they
 *     only mean anything together with the cluster-id sets the chart response
 *     hands back. Rehydrating costs a second render pass and a second request,
 *     which is the exact duplicate round App.tsx just got rid of.
 *   - industryDrillPath / industryLeaf — the API param exists
 *     (`industriesNarrow`), but reconstructing the PATH needs the industry
 *     tree, which only arrives with chart-data. A partial rehydrate would show
 *     a breadcrumb that disagrees with the active filter. The upgrade path is
 *     a leaf-only param, which needs no tree.
 *   - The open project modal — the audit argues against per-project URLs.
 *   - Map fullscreen — owned by InvestmentMap's own history entry.
 *   - View chrome (chart view, bubble mode, table tab, page, sort) — every
 *     param written is a URL variant the host page's cache, analytics and
 *     plugins have to tolerate. Earn them one at a time; the filters carry
 *     the value.
 */

import { DEFAULT_INVESTMENT_TYPES } from './investmentTiers';
import type { FilterState } from '../types';

/** The params this module owns. Everything else on the URL is the host's. */
export const OWN_PARAMS = [
  'dateFrom',
  'dateTo',
  'federalStates',
  'investmentTypes',
  'investmentStatuses',
  'industries',
  'companies',
  'search',
  'spotlight',
] as const;

/** Array params, all pipe-joined exactly as buildParams sends them. */
const LIST_PARAMS: ReadonlyArray<[keyof FilterState, string]> = [
  ['federalStates', 'federalStates'],
  ['investmentStatuses', 'investmentStatuses'],
  ['industries', 'industries'],
  ['companies', 'companies'],
];

// Caps on decoded input. Generous enough that no real view hits them, tight
// enough that a hand-mangled URL can't turn into a monster query.
const MAX_LIST_ITEMS = 50;
const MAX_ITEM_CHARS = 200;
const SPOTLIGHT_RE = /^[a-z0-9-]{1,50}$/;       // mirrors FilterQuerySchema
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface UrlState {
  /** A complete FilterState — anything absent or invalid falls back to default. */
  filters: FilterState;
  /** ISO dates, or null meaning "follow the data's own bounds". */
  dateFrom: string | null;
  dateTo: string | null;
}

export function emptyUrlFilters(): FilterState {
  return {
    dateFrom: null,
    dateTo: null,
    federalStates: [],
    investmentTypes: [...DEFAULT_INVESTMENT_TYPES],
    investmentStatuses: [],
    industries: [],
    companies: [],
    search: '',
    spotlight: '',
  };
}

/**
 * True when the boot URL narrows nothing — the first render will be the whole
 * dataset: a full 50-row page, every industry in the bar chart, every point on
 * the map.
 *
 * The boot reservation (LoadingContainer in App.tsx) needs this because the two
 * cases differ by an order of magnitude below 600px, where the table renders as
 * stacked cards: the default view settles at ~13000px there, a single-result
 * search at 2816px. One box cannot serve both without overshooting one of them,
 * and overshooting is the worse error — it shifts the page the other way.
 *
 * Deliberately strict: anything in the URL that can drop rows counts as
 * narrowing, including a date window, since the reservation must never be the
 * optimistic guess.
 */
export function isFullDatasetView(state: UrlState): boolean {
  const f = state.filters;
  return (
    state.dateFrom === null &&
    state.dateTo === null &&
    f.dateFrom === null &&
    f.dateTo === null &&
    f.search === '' &&
    f.spotlight === '' &&
    f.federalStates.length === 0 &&
    f.investmentStatuses.length === 0 &&
    f.industries.length === 0 &&
    f.companies.length === 0 &&
    sameSet(f.investmentTypes, DEFAULT_INVESTMENT_TYPES)
  );
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every(v => bs.has(v));
}

function decodeList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split('|')
    .map(v => v.trim())
    .filter(v => v.length > 0 && v.length <= MAX_ITEM_CHARS)
    .slice(0, MAX_LIST_ITEMS);
}

export interface EncodeUrlStateInput {
  filters: FilterState;
  fromTimestamp: number;
  toTimestamp: number;
  /** The data's own bounds, from /api/meta. 0 while meta is still in flight. */
  minTimestamp: number;
  maxTimestamp: number;
}

function isoDay(ts: number): string {
  return new Date(ts).toISOString().split('T')[0]!;
}

/**
 * Serialise a view into a query string (no leading '?').
 *
 * Defaults are omitted, so an untouched view produces the empty string and the
 * host page's address is left exactly as it was. Date bounds ride only when
 * they NARROW the range — the same rule App.tsx applies before sending them to
 * the API, so a shared link never carries a filter that does nothing.
 */
export function encodeUrlState(input: EncodeUrlStateInput): string {
  const { filters, fromTimestamp, toTimestamp, minTimestamp, maxTimestamp } = input;
  const p = new URLSearchParams();

  if (filters.spotlight) p.set('spotlight', filters.spotlight);

  for (const [key, param] of LIST_PARAMS) {
    const value = filters[key] as string[];
    if (value.length > 0) p.set(param, value.join('|'));
  }

  // Compared as a SET: investmentTiers derives the tier toggles from this
  // array, and a merely reordered list is the same view.
  if (!sameSet(filters.investmentTypes, DEFAULT_INVESTMENT_TYPES)) {
    p.set('investmentTypes', filters.investmentTypes.join('|'));
  }

  if (filters.search) p.set('search', filters.search);

  if (fromTimestamp > 0 && minTimestamp > 0 && fromTimestamp > minTimestamp) {
    p.set('dateFrom', isoDay(fromTimestamp));
  }
  if (toTimestamp > 0 && maxTimestamp > 0 && toTimestamp < maxTimestamp) {
    p.set('dateTo', isoDay(toTimestamp));
  }

  return p.toString();
}

/** Read a view out of a query string. Never throws; bad input is dropped. */
export function decodeUrlState(search: string): UrlState {
  const p = new URLSearchParams(search);
  const filters = emptyUrlFilters();

  // A slug the API would reject must not reach it: the 400 would take down
  // every subsequent request, not just this filter.
  const spotlight = p.get('spotlight')?.trim() ?? '';
  if (SPOTLIGHT_RE.test(spotlight)) filters.spotlight = spotlight;

  for (const [key, param] of LIST_PARAMS) {
    const value = decodeList(p.get(param));
    if (value.length > 0) (filters[key] as string[]) = value;
  }

  const types = decodeList(p.get('investmentTypes'));
  if (types.length > 0) filters.investmentTypes = types;

  // The schema's lower bound; SearchInput never commits a shorter term either.
  const search2 = p.get('search')?.trim() ?? '';
  if (search2.length >= 2 && search2.length <= MAX_ITEM_CHARS) filters.search = search2;

  const rawFrom = p.get('dateFrom')?.trim() ?? '';
  const rawTo = p.get('dateTo')?.trim() ?? '';
  const dateFrom = ISO_DATE_RE.test(rawFrom) ? rawFrom : null;
  const dateTo = ISO_DATE_RE.test(rawTo) ? rawTo : null;

  return { filters, dateFrom, dateTo };
}

/**
 * Merge our params into a query string, leaving every other param alone.
 *
 * The widget does not own this URL. Replacing the whole query string would
 * drop the host's own params — utm_*, fbclid, a WordPress preview token — on
 * the first filter click, invisibly.
 */
export function mergeOwnParams(currentSearch: string, ownQuery: string): string {
  const merged = new URLSearchParams(currentSearch);
  for (const name of OWN_PARAMS) merged.delete(name);
  for (const [k, v] of new URLSearchParams(ownQuery)) merged.append(k, v);
  return merged.toString();
}

/**
 * The query string a spotlight card links to.
 *
 * A spotlight is a view PRESET (handleSpotlightClick resets everything else),
 * so the link clears all of our other params and keeps only the host's.
 * `null` gives the "clear this spotlight" target for the active card.
 */
export function spotlightQuery(slug: string | null, currentSearch: string): string {
  return mergeOwnParams(currentSearch, slug ? `spotlight=${encodeURIComponent(slug)}` : '');
}

/** `?…` when there is anything to write, otherwise '' — never a bare '?'. */
export function toSearchString(query: string): string {
  return query ? `?${query}` : '';
}
